<!-- <svelte:options customElement="clever-search-chat-result-item" /> -->

<script lang="ts">
  import { beforeUpdate } from 'svelte';
  import { ApiTypes } from '../enums/api.enum';
  import { ConfigStore } from '../store/config';
  import {
    state_busyApi,
    state_conversationResults,
    state_headerHeight,
    state_questionRelatedSearchSuggestions,
    state_scrollingBehavior,
    state_searchString,
  } from '../store/state';
  import { lazyLoad } from '../actions/lazy-img-load';

  import { fade, fly } from 'svelte/transition';
  import { _, locale } from 'svelte-i18n';
  import { WindowsCustomEventTypes } from '../enums/windows-custom-event.enum';
  import { WidgetType } from '../enums/widget-type.enum';
  import type { IRequestSearch } from '../interfaces/request-search-question.interface';
  import { AnswerQuestionThemeTypes } from '../enums/themes/answer-question-theme-type.enum';
  import Rating from './rating.svelte';
  import QuestionRelatedSuggestionComponents from './question-related-search.svelte';
  import BusyComponent from './busy.svelte';
  import GreetingTheme from './themes/greeting-theme.svelte';
  import { ConversationHistoryHelper } from '../helpers/conversation-history-helper';
  import type { IOAuthAuthorizationRequest } from '../interfaces/oauth-authorization.interface';
  import ChatKitWidgetRenderer from './chatkit-widget-renderer.svelte';
  import { MarkupHelper } from '../helpers/markup-helper';

  export let index;
  export let indexOfLastItem;

  $: isSearchBusy = $state_busyApi.some((x) =>
    [
      ApiTypes.postQuestion,
      ApiTypes.streamPOSTQuestion, // added 07/24
      ApiTypes.postOption,
      ApiTypes.getOption,
      ApiTypes.getThread,
    ].includes(x)
  );

  $: conversation = $state_conversationResults[index];

  const md = new MarkupHelper();

  const getCurrentReasoningTrace = () => {
    const chunks = conversation?.answer_chunks ?? [];
    if (!chunks.length) return conversation?.reasoning ?? '';

    let latestTrace = '';
    let currentTrace = '';
    chunks.forEach((chunk) => {
      // Keep showing the completed pass until the next pass has meaningful
      // content. Tool execution can leave this next pass empty for a while.
      if (chunk.content === '\n\n' && chunk.reasoning === '\n\n') {
        if (currentTrace.trim()) latestTrace = currentTrace;
        currentTrace = '';
      } else {
        currentTrace += chunk.reasoning ?? '';
      }
    });

    return currentTrace.trim() ? currentTrace : latestTrace;
  };

  $: activeInteractionStart = (() => {
    const turns = $state_conversationResults;
    for (let turnIndex = turns.length - 1; turnIndex >= 0; turnIndex--) {
      // Tool continuations have an empty question and belong to the preceding
      // user interaction. Whitespace-only user messages remain boundaries.
      if (turns[turnIndex]?.question?.length || turns[turnIndex]?.is_greeting) {
        return turnIndex;
      }
    }
    return 0;
  })();

  $: activeInteraction = $state_conversationResults.slice(
    activeInteractionStart
  );
  $: latestReasoningIndex = activeInteraction.reduce(
    (latestIndex, turn, turnOffset) =>
      turn.reasoning?.trim()
        ? activeInteractionStart + turnOffset
        : latestIndex,
    -1
  );
  $: activeInteractionHasAnswer = activeInteraction.some(
    (turn) => !!turn.answer
  );
  $: shouldShowReasoningStatus =
    isSearchBusy &&
    !activeInteractionHasAnswer &&
    index === latestReasoningIndex;

  $: reasoningToDisplay = (() => {
    if (!conversation?.reasoning || $ConfigStore.reasoningMode === 'none') {
      return '';
    }

    if ($ConfigStore.reasoningMode === 'full') {
      return conversation.reasoning;
    }

    if (!shouldShowReasoningStatus) return '';
    return getCurrentReasoningTrace();
  })();

  $: formattedReasoning = md.render(
    reasoningToDisplay,
    $ConfigStore.whitelistedDomains
  );

  $: isHidden = conversation?.hideOnChat;

  let dataSource: {
    sourceId: string;
    content: string[];
    title: string;
    url: string;
    image_url?: string;
    localId?: number;
  }[] = [];

  beforeUpdate(() => {
    if (!dataSource.length) isMultipleSources();
  });

  const isMultipleSources = () => {
    //console.log("isMultipleSources", conversation);
    if (conversation?.is_streaming) return; // only check at the end of the stream
    try {
      dataSource = [];
      for (let item of conversation.document_data ?? []) {
        //console.log("item", item);
        const hasSourceIndex = dataSource.findIndex(
          (x) => x.sourceId === item?.source_id && !!item?.url?.length
        );

        if (hasSourceIndex > -1) {
          //console.log("hasSourceIndex", hasSourceIndex);
          dataSource[hasSourceIndex].content.push(item.content);
        } else {
          //console.log("item.url.length", item?.url?.length);
          if (!!item?.url?.length)
            dataSource.push({
              sourceId: item.source_id ?? '',
              content: [item.content],
              title: item.title,
              url: item.url,
              localId: item.local_id,
              image_url: item?.image_url,
            });
        }
      }

      dataSource.sort((a, b) => (a.localId ?? 0) - (b.localId ?? 0));
    } catch {
      dataSource = [];
    }
  };

  const sourcesHasImages = (sources: any[]): boolean => {
    return sources.findIndex((x) => x?.image_url) > -1;
  };

  const addToCart = (e: MouseEvent, addToCartText: string, sourceitem: any) => {
    e.preventDefault();

    const data: IRequestSearch = {
      query: addToCartText + ': ' + sourceitem.title,
      flags: $ConfigStore.customAttributes?.flags,
      cookies: $ConfigStore.customAttributes?.cookies,
      url: $ConfigStore.customAttributes?.url,
      requestType: 'turnX',
    };

    window.dispatchEvent(
      new CustomEvent(WindowsCustomEventTypes.requestSearch, {
        detail: data,
      })
    );

    if (
      ![
        WidgetType.chat,
        WidgetType.cb,
        WidgetType.cip,
        WidgetType.ce,
        WidgetType.cmt,
        WidgetType.ctt,
      ].includes($ConfigStore.widgetType) &&
      data.query
    ) {
      state_scrollingBehavior.set(!$state_scrollingBehavior);
      state_searchString.set(data.query);
    }
  };

  $: answerParagraphs = conversation?.answer
    ? conversation.answer
        .split(/<\/p>\s*<p[^>]*>/i)
        .map((p) => p.replace(/^<p[^>]*>|<\/p>\s*$/gi, '').trim())
        .filter((p) => p)
    : [];

  const streamErrorTranslationKey = (error: string): string => {
    switch (error) {
      case 'content_filter':
      case 'Content filter triggered':
      case 'GPT Content Filter Error':
        return 'chat_wrapper.error_content_filter';
      case 'too_many_requests':
      case 'Service unavailable due to high load':
      case 'Too Many Requests':
        return 'chat_wrapper.error_too_many_requests';
      default:
        return 'chat_wrapper.error_general';
    }
  };

  $: streamErrorMessage = conversation?.is_streamError
    ? $_(streamErrorTranslationKey(conversation.is_streamError))
    : undefined;

  const startAuthorization = (request: IOAuthAuthorizationRequest) => {
    const url = new URL(request.start_path, $ConfigStore.apiBaseUrl);
    window.open(
      url.href,
      `gqa-oauth-${request.transaction_id}`,
      'popup=yes,width=560,height=720'
    );
  };

  const cancelAuthorization = async (request: IOAuthAuthorizationRequest) => {
    const threadId =
      conversation.thread_id || ConversationHistoryHelper.getThreadId();
    if (!threadId) return;
    const url = new URL(
      `/1/oauth/authorization/${encodeURIComponent(request.transaction_id)}/cancel`,
      $ConfigStore.apiBaseUrl
    );
    const response = await fetch(url.href, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ thread_id: threadId }),
    });
    // The persistent authorization.cancelled event updates the matching turn.
    // Updating "last turn" here can overwrite the follow-up turn created by
    // that event when it arrives before this request completes.
    if (!response.ok) return;
  };

  const attachmentUrl = (id: string): string => {
    const url = new URL(
      `/1/attachment/${encodeURIComponent(id)}`,
      $ConfigStore.apiBaseUrl
    );
    const tenantId = $ConfigStore.serverConfig?.tenant_id;
    if (tenantId) url.searchParams.set('tenant', tenantId);
    return url.href;
  };
</script>

{#if !!conversation && !isHidden}
  <div
    style:--cs-header-height={$state_headerHeight + 'px'}
    class={`cs-chat-item cs-chat-item-${$ConfigStore.widgetType}
    ${indexOfLastItem && $ConfigStore.scrollBehavior ? 'cs-search-loading-chat-last' : ''}`}
  >
    {#if $state_conversationResults.length - 1 === 0 && conversation.is_greeting && $ConfigStore.widgetType === WidgetType.ce}
      <GreetingTheme />
    {:else}
      {#if conversation.question && !conversation.is_greeting}
        {#if $ConfigStore.isIdentifyName}
          <div aria-hidden="true" title="You" class="cs-q-character">You</div>
        {/if}
        {#if conversation.question_files?.length}
          <div class="cs-q-attachments">
            {#each conversation.question_files as file}
              <img
                src={file.previewUrl ?? attachmentUrl(file.id)}
                alt="Attachment"
              />
            {/each}
          </div>
        {/if}
        <div
          class={`cs-q
        ${$ConfigStore.customTheme.answerQuestionThemeTypes === AnswerQuestionThemeTypes.slides ? 'cs-slideFly' : 'cs-fadeInFly-1s '}`}
          style:background={$ConfigStore.customTheme.msgBubbleUserBg}
          style:color={$ConfigStore.customTheme.msgBubbleUserTextColor}
        >
          {conversation.question}
        </div>
      {/if}
      <!-- {#if conversation.answer} -->

      <div
        class={`cs-a-wrapper
      ${$ConfigStore.customTheme.answerQuestionThemeTypes === AnswerQuestionThemeTypes.slides ? 'cs-slideFly' : 'cs-fadeInFly-1s '}`}
      >
        {#if conversation.notices?.some((notice) => notice.code === 'history_reset')}
          <div class="cs-history-notice" role="status" aria-live="polite">
            {$ConfigStore.historyResetMessage}
          </div>
        {/if}
        {#if $ConfigStore.isIdentifyName && !conversation.is_greeting}
          <div
            aria-hidden="true"
            title={$ConfigStore.agentName}
            class="cs-a-character"
          >
            {$ConfigStore.agentName}
          </div>
        {/if}
        {#if formattedReasoning}
          <div
            class="cs-a cs-reasoning"
            aria-live={$ConfigStore.reasoningMode === 'status'
              ? 'polite'
              : undefined}
            style:background={$ConfigStore.customTheme.msgBubbleAgentBg}
            style:color={$ConfigStore.customTheme.msgBubbleAgentTextColor}
            style:opacity="0.4"
            style:font-size="0.7em"
            style:border="1px dotted {$ConfigStore.customTheme
              .msgBubbleAgentTextColor}"
          >
            {@html formattedReasoning}
          </div>
        {/if}

        {#if conversation.notes}
          {#each conversation.notes as note}
            <div class="cs-note">
              {@html note}
            </div>
          {/each}
        {/if}

        {#if conversation.authorization_requests?.length}
          {#each conversation.authorization_requests as authorization}
            <div
              class="cs-oauth-card"
              class:cs-oauth-card-status={authorization.status &&
                authorization.status !== 'pending'}
              style:--cs-oauth-surface={$ConfigStore.customTheme
                .msgBubbleAgentBg || '#fff'}
              style:--cs-oauth-text={$ConfigStore.customTheme
                .msgBubbleAgentTextColor || '#1f2937'}
              style:--cs-oauth-accent={$ConfigStore.customTheme
                .msgBubbleUserBg ||
                $ConfigStore.customTheme.badgeBg ||
                '#7123c0'}
              style:--cs-oauth-accent-text={$ConfigStore.customTheme
                .msgBubbleUserTextColor || '#fff'}
              role="group"
              aria-labelledby={`cs-oauth-title-${authorization.transaction_id}`}
            >
              {#if !authorization.status || authorization.status === 'pending'}
                <div class="cs-oauth-heading">
                  <span class="cs-oauth-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path
                        d="M7 10V8a5 5 0 0 1 10 0v2m-9 0h8a2 2 0 0 1 2 2v7H6v-7a2 2 0 0 1 2-2Z"
                      />
                      <path d="M12 14v2" />
                    </svg>
                  </span>
                  <div>
                    <div
                      class="cs-oauth-title"
                      id={`cs-oauth-title-${authorization.transaction_id}`}
                    >
                      Connect your account
                    </div>
                    <div class="cs-oauth-description">
                      Sign in to {authorization.display_name} to continue with your
                      request.
                    </div>
                  </div>
                </div>
                <div class="cs-oauth-actions">
                  <button
                    type="button"
                    class="cs-oauth-connect"
                    on:click={() => startAuthorization(authorization)}
                  >
                    Connect
                  </button>
                  <button
                    type="button"
                    class="cs-oauth-cancel"
                    on:click={() => cancelAuthorization(authorization)}
                  >
                    Cancel
                  </button>
                </div>
              {:else if authorization.status === 'succeeded'}
                <div class="cs-oauth-status cs-oauth-status-success">
                  <span class="cs-oauth-status-icon" aria-hidden="true">✓</span>
                  <div>
                    <div
                      class="cs-oauth-title"
                      id={`cs-oauth-title-${authorization.transaction_id}`}
                    >
                      Connected
                    </div>
                    <div class="cs-oauth-description">
                      Continuing with your request…
                    </div>
                  </div>
                </div>
              {:else if authorization.status === 'failed'}
                <div class="cs-oauth-status cs-oauth-status-error">
                  <span class="cs-oauth-status-icon" aria-hidden="true">!</span>
                  <div>
                    <div
                      class="cs-oauth-title"
                      id={`cs-oauth-title-${authorization.transaction_id}`}
                    >
                      Connection failed
                    </div>
                    <div class="cs-oauth-description">
                      Could not connect to {authorization.display_name}.
                      {authorization.error ?? ''}
                    </div>
                  </div>
                </div>
              {:else if authorization.status === 'cancelled'}
                <div class="cs-oauth-status">
                  <span class="cs-oauth-status-icon" aria-hidden="true">×</span>
                  <div>
                    <div
                      class="cs-oauth-title"
                      id={`cs-oauth-title-${authorization.transaction_id}`}
                    >
                      Connection cancelled
                    </div>
                    <div class="cs-oauth-description">
                      Your request was not continued.
                    </div>
                  </div>
                </div>
              {:else}
                <div
                  class="cs-oauth-title"
                  id={`cs-oauth-title-${authorization.transaction_id}`}
                >
                  Connection status unavailable
                </div>
              {/if}
            </div>
          {/each}
        {/if}

        {#if conversation.answer}
          {#each answerParagraphs as paragraph, paragraphIndex}
            <div
              class="cs-a"
              style:background={$ConfigStore.customTheme.msgBubbleAgentBg}
              style:color={$ConfigStore.customTheme.msgBubbleAgentTextColor}
            >
              {@html paragraph}
              {#if conversation?.is_streamError && paragraphIndex === answerParagraphs.length - 1}
                <div class="cs-msg-cancelled">
                  ({streamErrorMessage})
                </div>
              {/if}
              {#if conversation?.is_streamCancelled && paragraphIndex === answerParagraphs.length - 1}
                <span class="cs-msg-cancelled">(...)</span>
              {/if}
              {#if paragraphIndex === answerParagraphs.length - 1 && dataSource.length && !conversation.is_greeting && !conversation?.is_streaming && !conversation?.is_streamCancelled}
                <div class="cs-a-source-wrapper" in:fade|global>
                  {#if !sourcesHasImages(dataSource)}
                    <div
                      class="cs-title"
                      style:color={$ConfigStore.customTheme
                        .msgBubbleAgentTextColor}
                    >
                      {$_('chat_result_item.read_more')}
                    </div>
                  {/if}
                  {#each dataSource as sourceItem}
                    {#if !sourceItem?.image_url?.length}
                      <a
                        target={$ConfigStore.hyperlinkTargetType}
                        rel="noopener"
                        href={sourceItem.url}
                        class={sourceItem?.image_url?.length ? 'img-on' : ''}
                        aria-label={sourceItem.title}
                      >
                        {#if sourceItem.localId}
                          <span class="cs-citation-number"
                            >{sourceItem.localId}</span
                          >
                        {/if}
                        <span class="cs-source-title">{sourceItem?.title}</span>
                      </a>
                    {/if}
                  {/each}
                  {#if sourcesHasImages(dataSource)}
                    <div class="cs-img-source-wrapper">
                      {#each dataSource as sourceItem}
                        {#if sourceItem?.image_url?.length}
                          <a
                            target={$ConfigStore.hyperlinkTargetType}
                            rel="noopener"
                            href={sourceItem.url}
                            class={sourceItem?.image_url?.length
                              ? 'cs-img-on'
                              : ''}
                            aria-label={sourceItem?.title}
                          >
                            <img use:lazyLoad={sourceItem.image_url} alt="" />
                            {#if sourceItem.localId}
                              <span class="cs-citation-number"
                                >{sourceItem.localId}</span
                              >
                            {/if}
                            <div class="cs-source-title-wrapper">
                              <span
                                class="cs-source-title"
                                title={sourceItem.title}
                              >
                                {sourceItem.title}
                              </span>
                              <button
                                hidden={!$ConfigStore.showAddToCart &&
                                  !!$locale}
                                class="cs-add-to-cart"
                                on:click={(e) =>
                                  addToCart(e, $_('add_to_cart'), sourceItem)}
                                aria-label={$_('add_to_cart')}
                                title={$_('add_to_cart')}
                              >
                                <svg
                                  width="15"
                                  height="15"
                                  viewBox="0 0 10 10"
                                  fill="currentColor"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <g clip-path="url(#clip0_88_177)">
                                    <path
                                      d="M4.58317 3.75001H5.4165V2.50001H6.6665V1.66667H5.4165V0.416672H4.58317V1.66667H3.33317V2.50001H4.58317V3.75001ZM2.9165 7.5C2.45817 7.5 2.08734 7.875 2.08734 8.33334C2.08734 8.79167 2.45817 9.16667 2.9165 9.16667C3.37484 9.16667 3.74984 8.79167 3.74984 8.33334C3.74984 7.875 3.37484 7.5 2.9165 7.5ZM7.08317 7.5C6.62484 7.5 6.254 7.875 6.254 8.33334C6.254 8.79167 6.62484 9.16667 7.08317 9.16667C7.5415 9.16667 7.9165 8.79167 7.9165 8.33334C7.9165 7.875 7.5415 7.5 7.08317 7.5ZM2.98734 6.14584L2.99984 6.09584L3.37484 5.41667H6.479C6.7915 5.41667 7.0665 5.24584 7.20817 4.9875L8.8165 2.06667L8.0915 1.66667H8.08734L7.629 2.50001L6.479 4.58334H3.554L3.49984 4.47084L2.5665 2.50001L2.17067 1.66667L1.779 0.833338H0.416504V1.66667H1.24984L2.74984 4.82917L2.18734 5.85001C2.12067 5.96667 2.08317 6.10417 2.08317 6.25C2.08317 6.70834 2.45817 7.08334 2.9165 7.08334H7.9165V6.25H3.0915C3.03734 6.25 2.98734 6.20417 2.98734 6.14584Z"
                                      fill="currentColor"
                                    />
                                  </g>
                                  <defs>
                                    <clipPath id="clip0_88_177">
                                      <rect
                                        width="10"
                                        height="10"
                                        fill="white"
                                      />
                                    </clipPath>
                                  </defs>
                                </svg>
                              </button>
                            </div>
                          </a>
                        {/if}
                      {/each}
                    </div>
                  {/if}
                </div>
              {/if}
            </div>
          {/each}
        {/if}

        {#if conversation?.is_streamError && answerParagraphs.length === 0}
          <div
            class="cs-a"
            role="alert"
            style:background={$ConfigStore.customTheme.msgBubbleAgentBg}
            style:color={$ConfigStore.customTheme.msgBubbleAgentTextColor}
          >
            {streamErrorMessage}
          </div>
        {/if}

        {#if conversation.widgets?.length}
          <ChatKitWidgetRenderer widgets={conversation.widgets} />
        {/if}

        {#if conversation.answer && index == $state_conversationResults.length - 1 && !conversation.is_greeting && !conversation?.is_streaming && !conversation?.is_streamCancelled}
          <div in:fade|global={{ delay: 3000 }}>
            <Rating questionId={conversation.question_id} />
          </div>
        {/if}

        {#if indexOfLastItem && isSearchBusy && $ConfigStore.scrollBehavior}
          <div
            class="cs-search-loading-chat"
            class:cs-scrolling-behavior-loading={$ConfigStore.scrollBehavior}
            in:fade|global
          >
            <BusyComponent />
          </div>
        {/if}
      </div>
      {#if $ConfigStore.suggestQuestions !== 'never' && $state_questionRelatedSearchSuggestions && $state_questionRelatedSearchSuggestions.data && indexOfLastItem}
        <div class="cs-suggested-question-wrapper" in:fly|global>
          <QuestionRelatedSuggestionComponents />
        </div>
      {/if}
    {/if}
  </div>
{/if}

<style lang="scss">
  @use './../../scss/components/chat-wrapper';

  .cs-reasoning {
    :global(> :first-child) {
      margin-top: 0;
    }

    :global(> :last-child) {
      margin-bottom: 0;
    }
  }

  .cs-oauth-card {
    background: var(--cs-oauth-surface);
    border: 1px solid color-mix(in srgb, var(--cs-oauth-text) 16%, transparent);
    border-radius: 14px;
    box-shadow:
      0 1px 2px rgba(15, 23, 42, 0.08),
      0 8px 24px rgba(15, 23, 42, 0.08);
    color: var(--cs-oauth-text);
    margin: 12px 0;
    max-width: 460px;
    padding: 18px;
  }

  .cs-q-attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
    margin: 0 0 8px auto;
    max-width: min(100%, 320px);
  }

  .cs-q-attachments img {
    border-radius: 6px;
    max-height: 60px;
    max-width: 100%;
    object-fit: cover;
  }

  .cs-oauth-card-status {
    padding: 14px 16px;
  }

  .cs-oauth-heading,
  .cs-oauth-status {
    align-items: flex-start;
    display: flex;
    gap: 12px;
  }

  .cs-oauth-icon,
  .cs-oauth-status-icon {
    align-items: center;
    background: color-mix(
      in srgb,
      var(--cs-oauth-accent) 12%,
      var(--cs-oauth-surface)
    );
    border-radius: 10px;
    color: var(--cs-oauth-accent);
    display: inline-flex;
    flex: 0 0 38px;
    height: 38px;
    justify-content: center;
  }

  .cs-oauth-icon svg {
    fill: none;
    height: 20px;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 1.8;
    width: 20px;
  }

  .cs-oauth-title {
    font-size: 16px;
    font-weight: 650;
    line-height: 1.3;
  }

  .cs-oauth-description {
    font-size: 14px;
    line-height: 1.45;
    margin-top: 3px;
    opacity: 0.72;
  }

  .cs-oauth-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    margin-top: 18px;
  }

  .cs-oauth-actions button {
    border: 1px solid transparent;
    border-radius: 9px;
    cursor: pointer;
    font: inherit;
    font-size: 14px;
    font-weight: 650;
    min-height: 40px;
    padding: 9px 17px;
    transition:
      box-shadow 120ms ease,
      opacity 120ms ease,
      transform 120ms ease;
  }

  .cs-oauth-actions button:hover {
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.14);
    transform: translateY(-1px);
  }

  .cs-oauth-actions button:active {
    box-shadow: none;
    transform: translateY(0);
  }

  .cs-oauth-actions button:focus-visible {
    outline: 3px solid
      color-mix(in srgb, var(--cs-oauth-accent) 28%, transparent);
    outline-offset: 2px;
  }

  .cs-oauth-connect {
    background: var(--cs-oauth-accent);
    color: var(--cs-oauth-accent-text);
  }

  .cs-oauth-cancel {
    background: transparent;
    border-color: color-mix(
      in srgb,
      var(--cs-oauth-text) 24%,
      transparent
    ) !important;
    color: var(--cs-oauth-text);
  }

  .cs-oauth-status {
    align-items: center;
  }

  .cs-oauth-status-icon {
    border-radius: 50%;
    flex-basis: 30px;
    font-size: 17px;
    font-weight: 700;
    height: 30px;
  }

  .cs-oauth-status-error .cs-oauth-status-icon {
    background: rgba(185, 28, 28, 0.1);
    color: #b91c1c;
  }
</style>
